/**
 * Display helpers shared across the payroll screens, as a Vue 2 mixin so
 * templates can call them directly — the same shape as `attendanceFormat.js`.
 *
 * Formatting only. Nothing here adds up money.
 */

const RUN_STATUS = {
	draft: 'Draft',
	computed: 'Computed',
	approved: 'Approved',
	paid: 'Paid',
	cancelled: 'Cancelled',
}

const SLIP_STATUS = {
	draft: 'Draft',
	final: 'Awaiting payment',
	paid: 'Paid',
	cancelled: 'Cancelled',
}

export default {
	methods: {
		/** 15600 -> "15,600.00". Indian grouping, so 1,20,000 not 120,000. */
		money(value) {
			const number = Number(value || 0)
			return number.toLocaleString('en-IN', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			})
		},

		/** For column headers and totals where paise are noise. */
		moneyShort(value) {
			return Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })
		},

		/** 26.00 -> "26", 26.50 -> "26.5". Day counts read badly padded. */
		days(value) {
			const number = Number(value || 0)
			return Number.isInteger(number) ? String(number) : String(Number(number.toFixed(2)))
		},

		formatDate(value) {
			if (!value) return '—'
			const date = new Date(String(value).slice(0, 10))
			return Number.isNaN(date.getTime())
				? value
				: date.toLocaleDateString(undefined, {
						day: '2-digit',
						month: 'short',
						year: 'numeric',
				  })
		},

		/** YYYY-MM-DD in local time, without a UTC shift. */
		toIsoDate(date) {
			const offset = date.getTimezoneOffset() * 60000
			return new Date(date.getTime() - offset).toISOString().slice(0, 10)
		},

		currentMonth() {
			return this.toIsoDate(new Date()).slice(0, 7)
		},

		monthLabel(cycle) {
			if (!cycle) return ''
			const parts = String(cycle).split('-').map(Number)
			return new Date(parts[0], parts[1] - 1, 1).toLocaleDateString(undefined, {
				month: 'long',
				year: 'numeric',
			})
		},

		shiftMonthValue(cycle, delta) {
			const parts = String(cycle).split('-').map(Number)
			const date = new Date(parts[0], parts[1] - 1 + delta, 1)
			return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
		},

		runStatusLabel(status) {
			return RUN_STATUS[status] || status
		},

		slipStatusLabel(status) {
			return SLIP_STATUS[status] || status
		},

		runStatusClass(status) {
			switch (status) {
				case 'approved':
					return 'badge bg-primary'
				case 'paid':
					return 'badge bg-success'
				case 'computed':
					return 'badge bg-info text-dark'
				case 'cancelled':
					return 'badge bg-secondary'
				default:
					return 'badge bg-warning text-dark'
			}
		},

		slipStatusClass(status) {
			switch (status) {
				case 'paid':
					return 'badge bg-success'
				case 'final':
					return 'badge bg-primary'
				case 'cancelled':
					return 'badge bg-secondary'
				default:
					return 'badge bg-warning text-dark'
			}
		},

		/**
		 * Percentage change against last period, or null when there is nothing
		 * to compare. A variance column catches more payroll mistakes before
		 * approval than any other single check, so it is worth the arithmetic.
		 */
		variance(current, previous) {
			if (previous === null || previous === undefined || Number(previous) === 0) return null
			return ((Number(current) - Number(previous)) / Number(previous)) * 100
		},

		/** Only a real swing is worth colouring; small drift is normal. */
		varianceClass(percent) {
			if (percent === null) return 'text-muted'
			if (Math.abs(percent) < 5) return 'text-muted'
			return percent > 0 ? 'text-success' : 'text-danger'
		},
	},
}
